import {
  FetchAlpacaAccountByUserIdResponse,
  FetchTradingAccountResponse,
  FetchAlpacaEventsRequest,
  FetchAlpacaEventsResponse,
  FetchAlpacaOrdersByMadkholOrderIdResponse,
  FetchAlpacaOrdersResponse,
  FetchAlpacaTransfersResponse,
} from '../types/alpaca';
import { PaginatedRequest } from '../types/api';
import { toQueryParams } from '../utils/request';
import { ApiService } from './common/ApiService';

class AlpacaService extends ApiService {
  pathname = 'admin/alpaca';

  fetchEvents = async (data: FetchAlpacaEventsRequest) =>
    this.get<FetchAlpacaEventsResponse>(`${this.pathname}/events?${toQueryParams(data)}`);

  fetchOrders = async (data: PaginatedRequest) =>
    this.get<FetchAlpacaOrdersResponse>(`${this.pathname}/orders?${toQueryParams(data)}`);

  fetchTransfers = async (data: PaginatedRequest) =>
    this.get<FetchAlpacaTransfersResponse>(`${this.pathname}/transfers?${toQueryParams(data)}`);

  fetchOrdersByMadkholOrderId = async (madkholOrderId: string) =>
    this.get<FetchAlpacaOrdersByMadkholOrderIdResponse>(`${this.pathname}/orders/${madkholOrderId}`);

  sendOrderToJournalQueue = async (madkholOrderId: string, useMock?: boolean) =>
    this.post(`${this.pathname}/manual/order/${madkholOrderId}/journal-queue${useMock ? '?manualMock=true' : ''}`, {});

  sendOrderToCompleteQueue = async (madkholOrderId: string, useMock?: boolean) =>
    this.post(`${this.pathname}/manual/order/${madkholOrderId}/complete-queue${useMock ? '?manualMock=true' : ''}`, {});

  sendOrderToTransferQueue = async (madkholOrderId: string, useMock?: boolean) =>
    this.post(`${this.pathname}/manual/order/${madkholOrderId}/transfer-queue${useMock ? '?manualMock=true' : ''}`, {});

  sendOrderToOrderQueue = async (madkholOrderId: string, useMock?: boolean) =>
    this.post(`${this.pathname}/manual/order/${madkholOrderId}/order-queue${useMock ? '?manualMock=true' : ''}`, {});

  triggerTransferCreatedToCompleted = async (madkholOrderId: string) =>
    this.post(
      `${this.pathname}/manual/order/${madkholOrderId}/trigger-transfer-created-to-completed?manualMock=true`,
      {},
    );

  triggerJournalStatus = async (journalId: string, body?: { statusTo?: string; netAmount?: number }) =>
    this.post(`${this.pathname}/manual/journal/${journalId}/trigger-status?manualMock=true`, body ?? {});

  triggerTransferStatus = async (transferId: number, body: { statusTo?: string; amountUsd: number }) =>
    this.post(`${this.pathname}/manual/transfer/${transferId}/trigger-status?manualMock=true`, body);

  simulateNtaSubscription = async (transferId: number, body: { netAmountUsd: number }) =>
    this.post(`${this.pathname}/manual/nta/subscription/${transferId}?manualMock=true`, body);

  mockRedemptionBank = async (body: { transferId: number; amountSar: number }) =>
    this.post(`${this.pathname}/manual/redemption/outgoing-transfer/mock?manualMock=true`, body);

  triggerTradeEvent = async (
    orderId: number,
    body?: { filledQty?: number; filledAvgPrice?: number; notionalUsd?: number },
  ) => this.post(`${this.pathname}/manual/order/${orderId}/trigger-trade-event?manualMock=true`, body ?? {});

  checkJournalStatus = async (journalId: string) =>
    this.get<{ data: { journalId: string; alpacaStatus?: string; orderStep?: string; synced: boolean } }>(
      `${this.pathname}/journal/${journalId}/check-status`,
    );

  fetchAccountByUserId = async (userId: number) =>
    this.get<FetchAlpacaAccountByUserIdResponse>(`${this.pathname}/account/${userId}`);

  createAccount = async (userId: number) => this.post(`${this.pathname}/account`, { userId });

  fetchTradingAccount = async () => this.get<FetchTradingAccountResponse>(`${this.pathname}/trading-account`);
}

export const alpacaService = new AlpacaService();
